import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Grid, Card, CardContent,
  TextField, Button, MenuItem, FormControl, InputLabel,
  Select, Divider, Snackbar, Alert, CircularProgress,
} from '@mui/material';
import LaptopIcon from '@mui/icons-material/LaptopMac';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import TabletIcon from '@mui/icons-material/TabletMac';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { saveProductRequest } from '../services/db';
import { notifyInternal, notifyCustomer } from '../services/email';
import { productRequestEmail } from '../services/emailTemplates';
import { useCart } from '../context/CartContext';

const categories = [
  { value: 'Laptop',    label: 'Laptop',    icon: LaptopIcon },
  { value: 'Desktop',   label: 'Desktop',   icon: DesktopWindowsIcon },
  { value: 'Tablet',    label: 'Tablet',    icon: TabletIcon },
  { value: 'Phone',     label: 'Phone',     icon: PhoneAndroidIcon },
  { value: 'Accessory', label: 'Accessory', icon: DevicesOtherIcon },
  { value: 'Other',     label: 'Other',     icon: DevicesOtherIcon },
];

const brandsByCategory = {
  Laptop:    ['Apple', 'Dell', 'HP', 'Lenovo', 'ASUS', 'Acer', 'Microsoft', 'MSI', 'Samsung', 'Razer', 'LG', 'Toshiba', 'Other'],
  Desktop:   ['Dell', 'HP', 'Lenovo', 'ASUS', 'Acer', 'Apple', 'Custom Built', 'Other'],
  Tablet:    ['Apple (iPad)', 'Samsung', 'Microsoft (Surface)', 'Lenovo', 'Amazon (Fire)', 'Huawei', 'Other'],
  Phone:     ['Apple (iPhone)', 'Samsung', 'Google (Pixel)', 'OnePlus', 'Xiaomi', 'Motorola', 'Sony', 'Nokia', 'Other'],
  Accessory: ['Logitech', 'Apple', 'Samsung', 'Microsoft', 'Anker', 'Bose', 'Sony', 'JBL', 'Belkin', 'Other'],
  Other:     ['Other'],
};

const modelsByBrand = {
  // ── Laptops ──────────────────────────────────────────────────────────────────
  'Laptop_Apple':     ['MacBook Air 13" M1', 'MacBook Air 13" M2', 'MacBook Air 13" M3', 'MacBook Air 15" M2', 'MacBook Air 15" M3', 'MacBook Pro 13"', 'MacBook Pro 14" M3', 'MacBook Pro 14" M3 Pro', 'MacBook Pro 16" M3 Pro', 'MacBook Pro 16" M3 Max', 'Other'],
  'Laptop_Dell':      ['XPS 13 Plus', 'XPS 15', 'XPS 17', 'Inspiron 14', 'Inspiron 15 3000', 'Inspiron 15 5000', 'Latitude 5540', 'Latitude 7440', 'Latitude 9440', 'Alienware m16', 'Alienware m18 R2', 'Vostro 15', 'Other'],
  'Laptop_HP':        ['Spectre x360 14', 'Spectre x360 16', 'Envy x360 15', 'EliteBook 840 G10', 'EliteBook 1040 G10', 'ProBook 450 G10', 'OMEN 16', 'Pavilion 15', 'Dragonfly G4', 'Other'],
  'Laptop_Lenovo':    ['ThinkPad X1 Carbon Gen 12', 'ThinkPad T14 Gen 5', 'ThinkPad E16', 'IdeaPad 5 Pro', 'IdeaPad Slim 5', 'Yoga 9i', 'Yoga 7i', 'Legion 5i Gen 9', 'Legion 7i Gen 9', 'LOQ 15', 'Other'],
  'Laptop_ASUS':      ['ZenBook 14 OLED', 'ZenBook Pro 16X', 'VivoBook 15', 'ROG Zephyrus G14', 'ROG Zephyrus G16', 'ROG Strix G16', 'TUF Gaming A15', 'TUF Gaming F15', 'ExpertBook B9', 'Other'],
  'Laptop_Acer':      ['Swift 3', 'Swift Go 14', 'Aspire 5', 'Predator Helios 16', 'Predator Helios 18', 'Nitro 5', 'Spin 5', 'Other'],
  'Laptop_Microsoft': ['Surface Laptop 5', 'Surface Laptop 6', 'Surface Laptop Studio 2', 'Surface Pro 9', 'Surface Pro 10', 'Other'],
  'Laptop_MSI':       ['Raider GE78', 'Titan GT77', 'Stealth 16 Studio', 'Creator M16', 'Prestige 14 EVO', 'Katana 15', 'Cyborg 15', 'Other'],
  'Laptop_Samsung':   ['Galaxy Book4 Pro', 'Galaxy Book4 Pro 360', 'Galaxy Book3 Ultra', 'Galaxy Book3 360', 'Galaxy Book3', 'Other'],
  'Laptop_Razer':     ['Blade 14', 'Blade 15', 'Blade 16', 'Blade 18', 'Other'],
  'Laptop_LG':        ['Gram 14', 'Gram 16', 'Gram 17', 'Gram 2-in-1 14', 'UltraPC 15', 'Other'],
  'Laptop_Toshiba':   ['Portégé X30L', 'Tecra A50', 'Satellite Pro', 'Other'],

  // ── Desktops ─────────────────────────────────────────────────────────────────
  'Desktop_Dell':     ['OptiPlex 3000', 'OptiPlex 5000', 'OptiPlex 7000', 'XPS 8960', 'Alienware Aurora R16', 'Vostro 3900', 'Other'],
  'Desktop_HP':       ['EliteDesk 800 G9', 'ProDesk 400 G9', 'OMEN 45L', 'OMEN 25L', 'Envy All-in-One 34', 'Pavilion Desktop TP01', 'Other'],
  'Desktop_Lenovo':   ['ThinkCentre M90q', 'ThinkCentre M720q', 'IdeaCentre 5i', 'Legion Tower 5i Gen 8', 'Legion Tower 7i Gen 8', 'Other'],
  'Desktop_ASUS':     ['ROG Strix G35', 'ROG Huracan G21', 'ProArt Station D700', 'ExpertCenter D700', 'Other'],
  'Desktop_Acer':     ['Aspire TC', 'Nitro 50', 'Predator Orion 5000', 'Predator Orion 7000', 'Other'],
  'Desktop_Apple':    ['Mac mini M2', 'Mac mini M2 Pro', 'Mac mini M4', 'Mac Studio M2 Max', 'Mac Studio M2 Ultra', 'iMac 24" M3', 'Mac Pro M2 Ultra', 'Other'],
  'Desktop_Custom Built': ['Gaming Build', 'Workstation Build', 'Office Build', 'Describe in notes'],

  // ── Tablets ───────────────────────────────────────────────────────────────────
  'Tablet_Apple (iPad)':         ['iPad 10th Gen', 'iPad mini 6th Gen', 'iPad Air M1 11"', 'iPad Air M2 11"', 'iPad Air M2 13"', 'iPad Pro 11" M4', 'iPad Pro 13" M4', 'Other'],
  'Tablet_Samsung':              ['Galaxy Tab S9', 'Galaxy Tab S9+', 'Galaxy Tab S9 Ultra', 'Galaxy Tab S9 FE', 'Galaxy Tab A9', 'Galaxy Tab A9+', 'Other'],
  'Tablet_Microsoft (Surface)':  ['Surface Pro 9', 'Surface Pro 10', 'Surface Pro 9 5G', 'Surface Go 3', 'Surface Go 4', 'Other'],
  'Tablet_Lenovo':               ['Tab P12 Pro', 'Tab P11 Pro 12.6"', 'Tab M10 Plus Gen 3', 'Tab M9', 'Other'],
  'Tablet_Amazon (Fire)':        ['Fire HD 8', 'Fire HD 8 Plus', 'Fire HD 10', 'Fire HD 10 Plus', 'Fire Max 11', 'Other'],
  'Tablet_Huawei':               ['MatePad Pro 13.2"', 'MatePad Pro 11" 2024', 'MatePad 11.5"', 'MatePad Air', 'Other'],

  // ── Phones ────────────────────────────────────────────────────────────────────
  'Phone_Apple (iPhone)': ['iPhone 13', 'iPhone 13 Pro', 'iPhone 14', 'iPhone 14 Plus', 'iPhone 14 Pro', 'iPhone 14 Pro Max', 'iPhone 15', 'iPhone 15 Plus', 'iPhone 15 Pro', 'iPhone 15 Pro Max', 'iPhone 16', 'iPhone 16 Plus', 'iPhone 16 Pro', 'iPhone 16 Pro Max', 'Other'],
  'Phone_Samsung':        ['Galaxy S24', 'Galaxy S24+', 'Galaxy S24 Ultra', 'Galaxy S24 FE', 'Galaxy A55', 'Galaxy A35', 'Galaxy A15', 'Galaxy Z Fold 6', 'Galaxy Z Flip 6', 'Other'],
  'Phone_Google (Pixel)': ['Pixel 8', 'Pixel 8 Pro', 'Pixel 8a', 'Pixel 9', 'Pixel 9 Pro', 'Pixel 9 Pro XL', 'Pixel 9 Pro Fold', 'Other'],
  'Phone_OnePlus':        ['OnePlus 12', 'OnePlus 12R', 'OnePlus Open', 'OnePlus Nord 4', 'OnePlus Nord CE 4 Lite', 'Other'],
  'Phone_Xiaomi':         ['Xiaomi 14', 'Xiaomi 14 Ultra', 'Xiaomi 14T Pro', 'Redmi Note 13 Pro+', 'Redmi Note 13 Pro', 'POCO X6 Pro', 'Other'],
  'Phone_Motorola':       ['Moto G Power 5G', 'Moto G85', 'Moto Edge 50 Pro', 'Moto Edge 50 Ultra', 'Razr 50', 'Razr 50 Ultra', 'Other'],
  'Phone_Sony':           ['Xperia 1 VI', 'Xperia 5 VI', 'Xperia 10 VI', 'Xperia 1 V', 'Other'],
  'Phone_Nokia':          ['Nokia G42 5G', 'Nokia G22', 'Nokia XR21', 'Nokia T21', 'Nokia C32', 'Other'],
};

const budgetOptions = [
  'Under $300', '$300 – $500', '$500 – $800',
  '$800 – $1,200', '$1,200 – $2,000', '$2,000+', 'Flexible / No limit',
];

const ramOptions    = ['4GB', '8GB', '16GB', '32GB', '64GB', '128GB', 'Other'];
const storageOptions = ['64GB', '128GB', '256GB SSD', '512GB SSD', '1TB SSD', '2TB SSD', '4TB HDD', 'SSD + HDD Combo', 'Other'];
const processorOptions = ['Intel Core i3', 'Intel Core i5', 'Intel Core i7', 'Intel Core i9', 'AMD Ryzen 5', 'AMD Ryzen 7', 'AMD Ryzen 9', 'Apple M1', 'Apple M2', 'Apple M3', 'Other'];
const gpuOptions    = ['Integrated Graphics', 'NVIDIA RTX 3060', 'NVIDIA RTX 4070', 'NVIDIA RTX 4080', 'NVIDIA RTX 4090', 'AMD RX 6700', 'AMD RX 7900 XT', 'Other'];
const osOptions     = ['Windows 11', 'macOS', 'Linux', 'Chrome OS', 'Other'];
const screenOptions = ['11"', '13"', '14"', '15"', '16"', '17"', 'Other'];
const connectOptions = ['WiFi Only', 'WiFi + 4G LTE', 'WiFi + 5G'];

const inputSx = {
  '& .MuiOutlinedInput-root': {
    background: 'rgba(255,255,255,0.04)',
    '& fieldset': { borderColor: 'rgba(255,107,107,0.25)' },
    '&:hover fieldset': { borderColor: 'rgba(255,107,107,0.5)' },
    '&.Mui-focused fieldset': { borderColor: '#ff6b6b' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,232,232,0.6)' },
  '& .MuiInputBase-input': { color: '#ffe8e8' },
  '& .MuiSelect-icon': { color: 'rgba(255,232,232,0.6)' },
};

const SectionTitle = ({ children }) => (
  <Typography sx={{ color: '#ff6b6b', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: 1.5, mb: 2, mt: 1 }}>
    {children}
  </Typography>
);

const SelectField = ({ label, name, value, onChange, options, required }) => (
  <FormControl fullWidth required={required} sx={inputSx}>
    <InputLabel sx={{ color: 'rgba(255,232,232,0.6)' }}>{label}</InputLabel>
    <Select name={name} value={value} onChange={onChange} label={label}
      sx={{ color: '#ffe8e8', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,107,107,0.25)' } }}
      MenuProps={{ PaperProps: { sx: { bgcolor: '#2d3436', color: '#ffe8e8' } } }}
    >
      {options.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
    </Select>
  </FormControl>
);

export default function ProductRequest() {
  const location  = useLocation();
  const navigate  = useNavigate();
  const prefilled = location.state || {};
  const { addToCart } = useCart();

  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    category: prefilled.category || '',
    brand:    prefilled.brand    || '',
    model:    prefilled.model    || '',
    modelCustom: '',
    quantity: '1',
    budget:   '',
    ram: '', storage: '', processor: '', gpu: '', os: '',
    screenSize: '', connectivity: '',
    notes: '',
  });

  const [loading,  setLoading]  = useState(false);
  const [success,  setSuccess]  = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({
      ...f,
      [name]: value,
      ...(name === 'category' ? { brand: '', model: '', modelCustom: '', ram: '', storage: '', processor: '', gpu: '', os: '', screenSize: '', connectivity: '' } : {}),
      ...(name === 'brand'    ? { model: '', modelCustom: '' } : {}),
      ...(name === 'model'    ? { modelCustom: '' } : {}),
    }));
  };

  const isLaptop  = form.category === 'Laptop';
  const isDesktop = form.category === 'Desktop';
  const isTablet  = form.category === 'Tablet';
  const isPhone   = form.category === 'Phone';
  const showSpecs = isLaptop || isDesktop || isTablet || isPhone;

  const modelOptions = form.brand
    ? (modelsByBrand[`${form.category}_${form.brand}`] || null)
    : null;
  const finalModel = form.model === 'Other' ? form.modelCustom : form.model;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const refId = `PR-${Date.now().toString(36).toUpperCase()}`;
      const specs = {
        ...(form.ram        && { RAM: form.ram }),
        ...(form.storage    && { Storage: form.storage }),
        ...(form.processor  && { Processor: form.processor }),
        ...(form.gpu        && { GPU: form.gpu }),
        ...(form.os         && { 'Operating System': form.os }),
        ...(form.screenSize && { 'Screen Size': form.screenSize }),
        ...(form.connectivity && { Connectivity: form.connectivity }),
      };

      const request = {
        id: refId,
        firstName: form.firstName,
        lastName:  form.lastName,
        email:     form.email,
        phone:     form.phone,
        category:  form.category,
        brand:     form.brand,
        model:     finalModel,
        quantity:  form.quantity,
        budget:    form.budget,
        specs,
        notes:     form.notes,
        status:    'Pending',
        createdAt: new Date().toISOString(),
      };

      await saveProductRequest(request);

      const specText = Object.entries(specs).map(([k, v]) => `  ${k}: ${v}`).join('\n');
      await notifyInternal(
        `New Product Request — ${form.category} | ${refId}`,
        `Customer: ${form.firstName} ${form.lastName} <${form.email}>\nPhone: ${form.phone}\n\nCategory: ${form.category}\nBrand: ${form.brand}\nModel: ${finalModel}\nQuantity: ${form.quantity}\nBudget: ${form.budget}\n\nSpecs:\n${specText}\n\nNotes: ${form.notes || 'N/A'}`,
        form.email,
      );

      await notifyCustomer(
        form.email,
        `Product Request Confirmed — ${refId} | AkdmeTech`,
        productRequestEmail({
          firstName: form.firstName,
          refId,
          category:  form.category,
          brand:     form.brand,
          model:     finalModel,
          quantity:  form.quantity,
          budget:    form.budget,
          specs,
          notes:     form.notes,
        }),
      );

      addToCart({
        id: refId,
        name: [form.brand, finalModel].filter(Boolean).join(' ') || `${form.category} Request`,
        price: 0,
        isRequest: true,
        image: '/images/products/request-placeholder.jpg',
      });

      setSuccess(true);
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Something went wrong. Please try again.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Box sx={{ bgcolor: 'background.default', py: 10, minHeight: '70vh', display: 'flex', alignItems: 'center' }}>
        <Container maxWidth="sm">
          <Card sx={{ background: 'linear-gradient(135deg, rgba(255,107,107,0.1), rgba(255,217,61,0.15))', border: '1px solid rgba(255,107,107,0.2)', borderRadius: '16px', p: 2 }}>
            <CardContent sx={{ textAlign: 'center', py: 5 }}>
              <CheckCircleIcon sx={{ fontSize: 70, color: '#55efc4', mb: 2 }} />
              <Typography variant="h4" className="gradient-text" gutterBottom fontWeight={700}>Request Submitted!</Typography>
              <Typography color="text.secondary" sx={{ mb: 4, lineHeight: 1.8 }}>
                Thank you! We've received your product request and sent a confirmation to <strong style={{ color: '#ff6b6b' }}>{form.email}</strong>.<br />
                Our team will get back to you within 1–2 business days.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button variant="contained" onClick={() => navigate('/products')}
                  sx={{ background: 'linear-gradient(135deg, #ff6b6b, #ffd93d)', color: '#1a1f21', fontWeight: 700 }}>
                  Back to Products
                </Button>
                <Button variant="outlined" onClick={() => { setSuccess(false); setForm(f => ({ ...f, category: '', brand: '', model: '', budget: '', notes: '' })); }}
                  sx={{ borderColor: 'rgba(255,107,107,0.4)', color: '#ff6b6b' }}>
                  Submit Another
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', py: 8 }}>
      <Container maxWidth="md">
        <Typography variant="h2" align="center" className="gradient-text" gutterBottom sx={{ mb: 2 }}>
          Product Request
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 2, maxWidth: 650, mx: 'auto', lineHeight: 1.7 }}>
          Tell us what you need — brand, model, specs, and budget. We'll source it and get back to you within 1–2 business days.
        </Typography>

        {/* Category quick-select */}
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap', mb: 5 }}>
          {categories.map(({ value, label, icon: Icon }) => (
            <Button key={value} onClick={() => setForm(f => ({ ...f, category: value, brand: '', model: '', modelCustom: '', ram: '', storage: '', processor: '', gpu: '', os: '', screenSize: '', connectivity: '' }))}
              variant={form.category === value ? 'contained' : 'outlined'}
              startIcon={<Icon />}
              sx={{
                borderRadius: '10px', textTransform: 'none', fontWeight: 600,
                ...(form.category === value
                  ? { background: 'linear-gradient(135deg, #ff6b6b, #ffd93d)', color: '#1a1f21', border: 'none' }
                  : { borderColor: 'rgba(255,107,107,0.3)', color: 'rgba(255,232,232,0.7)', '&:hover': { borderColor: '#ff6b6b', color: '#ff6b6b' } }),
              }}>
              {label}
            </Button>
          ))}
        </Box>

        <Card component="form" onSubmit={handleSubmit}
          sx={{ background: 'linear-gradient(135deg, rgba(255,107,107,0.05), rgba(255,217,61,0.08))', border: '1px solid rgba(255,107,107,0.15)', borderRadius: '16px' }}>
          <CardContent sx={{ p: { xs: 3, md: 5 } }}>

            {/* Contact Info */}
            <SectionTitle>1. Your Contact Information</SectionTitle>
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}><TextField fullWidth required label="First Name" name="firstName" value={form.firstName} onChange={handleChange} sx={inputSx} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth required label="Last Name"  name="lastName"  value={form.lastName}  onChange={handleChange} sx={inputSx} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth required label="Email"      name="email"     type="email" value={form.email}  onChange={handleChange} sx={inputSx} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth required label="Phone"      name="phone"     value={form.phone}      onChange={handleChange} sx={inputSx} /></Grid>
            </Grid>

            <Divider sx={{ borderColor: 'rgba(255,107,107,0.1)', mb: 3 }} />

            {/* Product Info */}
            <SectionTitle>2. Product Information</SectionTitle>
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <SelectField label="Category" name="category" value={form.category} onChange={handleChange} options={categories.map(c => c.value)} required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <SelectField label="Brand" name="brand" value={form.brand} onChange={handleChange} options={brandsByCategory[form.category] || ['Other']} required={!!form.category} />
              </Grid>
              <Grid item xs={12} sm={8}>
                {modelOptions ? (
                  <SelectField
                    label="Model"
                    name="model"
                    value={form.model}
                    onChange={handleChange}
                    options={modelOptions}
                  />
                ) : (
                  <TextField
                    fullWidth
                    label="Model / Product Name"
                    name="model"
                    value={form.model}
                    onChange={handleChange}
                    placeholder="e.g. MacBook Pro 14-inch, Galaxy S24…"
                    sx={inputSx}
                  />
                )}
              </Grid>
              {form.model === 'Other' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Please describe the model you're looking for"
                    name="modelCustom"
                    value={form.modelCustom}
                    onChange={handleChange}
                    placeholder="Type the model name or describe what you need…"
                    sx={inputSx}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={4}>
                <TextField fullWidth required label="Quantity" name="quantity" type="number" value={form.quantity} onChange={handleChange} inputProps={{ min: 1 }} sx={inputSx} />
              </Grid>
              <Grid item xs={12}>
                <SelectField label="Budget Range" name="budget" value={form.budget} onChange={handleChange} options={budgetOptions} required />
              </Grid>
            </Grid>

            {/* Specs */}
            {showSpecs && (
              <>
                <Divider sx={{ borderColor: 'rgba(255,107,107,0.1)', mb: 3 }} />
                <SectionTitle>3. Specifications {' '}
                  <Typography component="span" sx={{ color: 'rgba(255,232,232,0.4)', fontSize: '0.75rem', fontWeight: 400, textTransform: 'none' }}>(optional — fill what applies)</Typography>
                </SectionTitle>
                <Grid container spacing={2.5} sx={{ mb: 3 }}>
                  {(isLaptop || isDesktop) && <>
                    <Grid item xs={12} sm={6}><SelectField label="RAM" name="ram" value={form.ram} onChange={handleChange} options={ramOptions} /></Grid>
                    <Grid item xs={12} sm={6}><SelectField label="Storage" name="storage" value={form.storage} onChange={handleChange} options={storageOptions} /></Grid>
                    <Grid item xs={12} sm={6}><SelectField label="Processor" name="processor" value={form.processor} onChange={handleChange} options={processorOptions} /></Grid>
                    {isDesktop && <Grid item xs={12} sm={6}><SelectField label="GPU / Graphics Card" name="gpu" value={form.gpu} onChange={handleChange} options={gpuOptions} /></Grid>}
                    <Grid item xs={12} sm={6}><SelectField label="Operating System" name="os" value={form.os} onChange={handleChange} options={osOptions} /></Grid>
                    {isLaptop && <Grid item xs={12} sm={6}><SelectField label="Screen Size" name="screenSize" value={form.screenSize} onChange={handleChange} options={screenOptions} /></Grid>}
                  </>}
                  {(isTablet || isPhone) && <>
                    <Grid item xs={12} sm={6}><SelectField label="Storage" name="storage" value={form.storage} onChange={handleChange} options={storageOptions} /></Grid>
                    <Grid item xs={12} sm={6}><SelectField label="Connectivity" name="connectivity" value={form.connectivity} onChange={handleChange} options={connectOptions} /></Grid>
                    {isTablet && <Grid item xs={12} sm={6}><SelectField label="Screen Size" name="screenSize" value={form.screenSize} onChange={handleChange} options={screenOptions} /></Grid>}
                  </>}
                </Grid>
              </>
            )}

            <Divider sx={{ borderColor: 'rgba(255,107,107,0.1)', mb: 3 }} />

            {/* Notes */}
            <SectionTitle>{showSpecs ? '4' : '3'}. Additional Requirements</SectionTitle>
            <TextField fullWidth multiline rows={4} label="Any other details, color preferences, or specific requirements…" name="notes" value={form.notes} onChange={handleChange} sx={{ ...inputSx, mb: 3 }} />

            <Button type="submit" fullWidth variant="contained" size="large" disabled={loading}
              sx={{ py: 1.8, background: 'linear-gradient(135deg, #ff6b6b, #ffd93d)', color: '#1a1f21', fontWeight: 800, fontSize: '1rem', borderRadius: '10px', '&:hover': { opacity: 0.92 } }}>
              {loading ? <CircularProgress size={24} sx={{ color: '#1a1f21' }} /> : 'Submit Product Request'}
            </Button>

          </CardContent>
        </Card>
      </Container>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
